import React, { useEffect } from 'react';
import { FormInstance, Grid, Skeleton, Space } from 'antd';
import { useRouter } from 'next/router';
import { jobOffer as jobOfferEntity } from '@vette/common-utils';
import { useJobOfferQuery, JobOfferFragment } from '@vette/data-access';
import {
  Title,
  FormItem,
  Select,
  Text,
  Paragraph,
  theme,
} from '@vette/ui-components';
import { growthbook, use404Redirect } from '@vette/frontend-utils';
import { FormType, FormItemNames } from './form';
import { KnockoutQuestions } from './KnockoutQuestions';
import { validations } from './validations';
import * as S from './styles';

type Props = {
  args: string[];
  isFormDisabled: boolean;
  form: FormInstance<FormType>;
  onJobOfferDisabled: (reason: jobOfferEntity.DisableErrorReason) => void;
  defaultData?: JobOfferFragment;
};

export const JobOfferDetail: React.FC<Props> = ({
  args,
  isFormDisabled,
  form,
  onJobOfferDisabled,
  defaultData,
}) => {
  const router = useRouter();
  const [jobOfferId] = args;

  const { xs } = Grid.useBreakpoint();

  const { data } = useJobOfferQuery({
    skip: defaultData != null,

    onCompleted: () => {
      if (data?.jobOffer?.__typename === 'JobOffer') {
        const {
          availability,
          client: { remainingVettes },
        } = data.jobOffer;

        growthbook.appendAttributes({
          company: data.jobOffer.companyName,
        });

        if (availability === 'DISABLED') {
          onJobOfferDisabled(availability);
        } else if (remainingVettes < 1) {
          onJobOfferDisabled('RAN_OUT_OF_VETTES');
        }
      }
    },
    variables: { jobOfferId },
  });

  const jobOffer = defaultData ?? data?.jobOffer;

  useEffect(() => {
    if (!defaultData && (!args.length || args.length > 2)) {
      router.replace('/404');
    }
  }, [args, router]);

  useEffect(() => {
    if (
      jobOffer?.__typename === 'JobOffer' &&
      (jobOffer.locations || []).length <= 1
    ) {
      form.setFields([
        {
          name: FormItemNames.location,
          touched: true,
          value: jobOffer.locations?.[0],
        },
      ]);
    }
  }, [data?.jobOffer, form]);

  use404Redirect(jobOffer?.__typename, jobOfferId ?? defaultData?.id);

  if (jobOffer?.__typename === 'JobOffer') {
    const hasLocationSelect = !!jobOffer.locations?.length;

    /* location provided in query could not match with allowed */
    const location = form.getFieldValue(FormItemNames.location);
    if (location && !jobOffer.locations.includes(location)) {
      form.setFieldsValue({ location: undefined });
    }

    return (
      <S.JobOfferDetailWrapper>
        <Space size={2} direction="vertical">
          <Title level={4}>
            {jobOffer?.companyName || jobOffer?.client?.companyName}
          </Title>
          <Space size={8} direction="vertical">
            <Title level={3}>{jobOffer?.title}</Title>
            <Text $weight={600} $size={18} $color={theme.colors.blue60}>
              {jobOffer?.salary}
            </Text>
          </Space>
        </Space>

        <Paragraph
          ellipsis={{ rows: xs ? 1.5 : 12, expandable: true, symbol: 'more' }}
          $size={15}
          $marginTop={16}
        >
          {jobOffer?.description}
        </Paragraph>

        {hasLocationSelect && (
          <FormItem
            name={FormItemNames.location}
            label={!xs && 'Location'}
            validateTrigger={['onSelect', 'onBlur']}
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <Select
              aria-label="Location"
              placeholder="Select Location"
              option={jobOffer?.locations}
              disabled={isFormDisabled}
              size="large"
            />
          </FormItem>
        )}

        {jobOffer.knockoutQuestions?.length ? (
          <S.FormItemKnockoutQuestion
            id="questionItemWrappedSubform"
            name={FormItemNames.knockoutAnswers}
            rules={validations.getPrecknockoutAnswersValidator(
              jobOffer.knockoutQuestions
            )}
          >
            <KnockoutQuestions
              form={form}
              questions={jobOffer.knockoutQuestions}
              disabled={isFormDisabled}
            />
          </S.FormItemKnockoutQuestion>
        ) : null}
      </S.JobOfferDetailWrapper>
    );
  }

  return <Skeleton active />;
};
